import { useState, useCallback, useRef } from "react";

export const useFormValidation = () => {
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(true);
  const fieldRefs = useRef({});
  const fieldLabels = useRef({});

  // Register field references
  const registerField = useCallback((fieldId, fieldRef, fieldLabel) => {
    fieldRefs.current[fieldId] = fieldRef;
    if (fieldLabel) {
      fieldLabels.current[fieldId] = fieldLabel;
    }
  }, []);

  // Get field label
  const getFieldLabel = useCallback((fieldId) => {
    return fieldLabels.current[fieldId] || fieldId;
  }, []);

  // Get all field labels
  const getAllFieldLabels = useCallback(() => {
    return { ...fieldLabels.current };
  }, []);

  // Validate all fields
  const validateAllFields = useCallback(() => {
    const errors = {};
    let hasErrors = false;

    Object.entries(fieldRefs.current).forEach(([fieldId, fieldRef]) => {
      if (fieldRef && fieldRef.current) {
        // Trigger validation on each field
        const error = fieldRef.current.validate();
        if (error) {
          errors[fieldId] = error;
          hasErrors = true;
        }
      }
    });

    setFormErrors(errors);
    setIsFormValid(!hasErrors);
    return !hasErrors;
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setFormErrors({});
    setIsFormValid(true);
  }, []);

  // Mark all fields as touched (to show validation errors)
  const touchAllFields = useCallback(() => {
    Object.values(fieldRefs.current).forEach((fieldRef) => {
      if (fieldRef && fieldRef.current && fieldRef.current.touch) {
        fieldRef.current.touch();
      }
    });
  }, []);

  return {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
    getAllFieldLabels,
  };
};
